<template>
  <div class="section" :class="$mq">
    <div class="title">{{ $t("step2.meterType.title") }}</div>
    <div class="container inline bottom">
      <EnergyButton
        type="mono"
        :title="$t('step2.meterType.meter-mono')"
        :active="hasMeterType('mono')"
        @click="setMeterType(['mono'])" />

      <EnergyButton
        type="tou"
        :title="$t('step2.meterType.meter-tou')"
        :active="hasMeterType('night')"
        @click="setMeterType(['day', 'night'])" />

      <EnergyButton
        type="exclusiveNight"
        :title="$t('step2.meterType.meter-exclusiveNight')"
        :active="hasMeterType('exclusiveNight')"
        @click="setMeterType(['exclusiveNight'])" />

      <EnergyButton
        v-if="hasElec"
        type="solar-panel"
        :title="$t('step2.meterType.solar-panel')"
        :active="hasSolarPanels"
        :radio="false"
        @click="hasSolarPanels = !hasSolarPanels" />

    </div>

    <div class="smart-meter-options" v-if="hasElec && area === 'vl'">
      <div class="container inline top">
        <CheckButton :active="hasSmartMeter" :title="$t('step2.meterType.smart-meter')" @click="hasSmartMeter = !hasSmartMeter" />

        <div class="options" v-if="hasSmartMeter">
          <CheckButton :active="hasQuarterRegime" :title="$t('step2.meterType.quarter-regime')" @click="hasQuarterRegime = !hasQuarterRegime" :left="true"/>
          <CheckButton :active="knowPeakLevel" :title="$t('step2.meterType.know-peak-level')" @click="knowPeakLevel = !knowPeakLevel" :left="true" />
        </div>
      </div>
      <div class="container inline" v-if="hasSmartMeter && knowPeakLevel">
        <label for="peakLevel">
          <h4>{{ $t("step2.meterType.peak-level") }}</h4>
          <ValidationProvider
            rules="required|commaFloatNumber:2.5,null"
            v-slot="{ errors }"
            :customMessages="{ required: $t('validation.errors.required')}"
          >
            <div :class="{error: errors.length}">
              <InputField
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                id="peakLevel"
                name="peakLevel"
                v-model="peakLevel"
                placeholder="2,5"
              />
              <span class="input-error">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </label>
      </div>
    </div>

    <template v-if="hasElec && hasSolarPanels && area === 'wl'">
      <div class="container inline margin-bottom">
        <div class="title">{{ $t("step2.meterType.solar-panel-power-question-certification") }}</div>
        <SwitchField
          :options="[$t('step2.meterType.solar-panel-power-question-certification-before2024'), $t('step2.meterType.solar-panel-power-question-certification-after2024')]"
          id="solarPanelsCertification"
          name="solarPanelsCertification"
          v-model="solarPanelsCertification"
          :default="solarPanelsCertification"
        />
      </div>
    </template>

    <template v-if="hasElec && hasSolarPanels">
      <div class="title">{{ $t("step2.meterType.solar-panel-power-question") }}</div>
      <SwitchField
        :options="[$t('step2.meterType.solar-panel-power-question-power'), $t('step2.meterType.solar-panel-power-question-number')]"
        id="solarPanelPowerQuestion"
        name="solarPanelPowerQuestion"
        v-model="knowSolarPanelPower"
        :default="knowSolarPanelPower"
      />
    </template>

    <div class="container inline center margin-top" v-if="hasElec && hasSolarPanels && knowSolarPanelPower">
      <label for="solarPanelPower">
        <h4>{{ $t("step2.meterType.solar-panel-power") }}</h4>
        <ValidationProvider
          rules="required|commaFloatNumber:0.1,10"
          v-slot="{ errors }"
          :customMessages="{ required: $t('step2.meterType.solar-power-required'),
          max_value: $t('step2.meterType.solar-power-max'),
          min_value: $t('step2.meterType.solar-power-min'),
           }"
        >
          <div :class="{error: errors.length}">
            <InputField
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              id="solarPanelPower"
              name="solarPanelPower"
              v-model="solarPanelsPower"
              placeholder="5"

            />

            <span class="input-error">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </label>
    </div>
    <div class="container inline center margin-top" v-else-if="hasElec && hasSolarPanels && !knowSolarPanelPower">
      <label for="solarPanelNumber">
        <h4>{{ $t("step2.meterType.solar-panel-power-number") }}</h4>
        <ValidationProvider
          rules="required|numeric|min_value:1|max_value:28"
          v-slot="{ errors }"
          :customMessages="{ required: $t('step2.meterType.solar-power-required'),
          min_value: $t('step2.meterType.solar-power-min'),
           }"
        >
          <div :class="{error: errors.length}">
            <InputField
              type="number"
              step="1"
              id="solarPanelNumber"
              name="solarPanelNumber"
              v-model="solarPanelsNumber"
              placeholder="14"
            />
            <span class="input-error">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </label>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import geoService from "../../services/geoService";
import EnergyButton from "../../components/Energy/EnergyButton.vue";
import CheckButton from "../../components/Form/CheckButton.vue";
import InputField from "../../components/Form/InputField.vue";
import SwitchField from "../../components/Form/SwitchField.vue";

export default {
  data() {
    return {
      knowSolarPanelPower: true,
      scenario: null
    };
  },
  computed: {
    ...mapState("consumption", ["meterType", "consumptions"]),
    ...mapState("calculation", ["area"]),
    ...mapGetters("energy", ["hasElec", "hasGas"]),
    ...mapGetters("common", ["isTestAchat"]),
    ...mapGetters("calculation", ["isBxl", "isFlanders", "isWallonia"]),
    hasSmartMeter: {
      get() {
        return this.$store.state.consumption.hasSmartMeter;
      },
      set(value) {
        this.$store.commit("consumption/setHasQuarterRegime", false);
        this.$store.commit("consumption/setKnowPeakLevel", false);
        this.$store.commit("consumption/setPeakLevel", null);
        this.$store.commit("consumption/setHasSmartMeter", value);
        this.$store.commit("consumption/setDoubleFlow", value);
        this.computeScenario();
      }
    },
    hasQuarterRegime: {
      get() {
        return this.$store.state.consumption.hasQuarterRegime;
      },
      set(value) {
        this.$store.commit("consumption/setHasQuarterRegime", value);
      }
    },
    knowPeakLevel: {
      get() {
        return this.$store.state.consumption.knowPeakLevel;
      },
      set(value) {
        this.$store.commit("consumption/setKnowPeakLevel", value);
        this.$store.commit("consumption/setPeakLevel", null);
      }
    },
    peakLevel: {
      get() {
        if (!this.$store.state.consumption.peakLevel) return "";
        return this.$store.state.consumption.peakLevel.replace(".", ",");
      },
      set(value) {
        this.$store.commit("consumption/setPeakLevel", value.replace(",", "."));
      }
    },
    fromFlanders() {
      return geoService.getRegion(this.$store.state.calculation.zipCode) === "BE-VLG"
    },
    hasSolarPanels: {
      get() {
        return this.$store.state.consumption.hasSolarPanels;
      },
      set(value) {
        this.$store.commit("consumption/setHasSolarPanels", value);
        this.computeScenario();
      }
    },
    solarPanelsPower: {
      get() {
        if(this.$store.state.consumption.solarPanelsPower != null)
          return this.$store.state.consumption.solarPanelsPower.replace('.', ',');
        return this.$store.state.consumption.solarPanelsPower;
      },
      set(value) {
        value = value.replace(',', '.');
        this.$store.commit("consumption/setSolarPanelsPower", value);
      }
    },
    solarPanelsCertification: {
      get() {
        return this.$store.state.consumption.solarPanelsCertification;
      },
      set(value) {
        this.$store.commit("consumption/setSolarPanelsCertification", value);
        this.computeScenario();
      }
    },
    solarPanelsNumber: {
      get() {
        return this.$store.state.consumption.solarPanelsNumber;
      },
      set(value) {
        if(value){
          this.solarPanelsPower = String(Math.round(((value * 0.35) + Number.EPSILON) * 100) / 100)
        }
        this.$store.commit("consumption/setSolarPanelsNumber", value);
      }
    },
  },
  methods: {
    hasMeterType(meterType) {
      return this.meterType.indexOf(meterType) !== -1;
    },
    removeMeterTypes(meterTypesToRemove) {
      let meterTypes = this.meterType.filter(
                          meterType => !meterTypesToRemove.includes(meterType)
                        );

      this.$store.commit("comparison/reset");
      this.$store.commit("consumption/setValid", false);
      this.$store.commit("consumption/setMeterType", meterTypes);
    },
    setMeterType(meterType) {
      let meterTypes = [];

      if (meterType.indexOf("exclusiveNight") === -1) {
        meterTypes = meterType.filter(meterType => meterType !== 'gas');
        if (this.hasGas) meterTypes.push("gas");
      } else {
        if(meterType.length === 1) {
          meterTypes = this.meterType;

          let index = meterTypes.indexOf("exclusiveNight");

          if (index === -1) {
            meterTypes.push("exclusiveNight");
          } else {
            meterTypes.splice(index, 1);
          }
        } else {
          meterTypes = meterType;
        }
      }

      if (
        this.hasMeterType('gas')
        && !meterTypes.includes("gas")
      ) {
        meterTypes.push('gas');
      }

      if(this.scenario === "COMM_OFFTAKE_AND_INJECTION"){
        if (meterTypes.indexOf('mono') !== -1 && meterTypes.indexOf('injectionBase') === -1) {
          meterTypes.push('injectionBase')
        } else {
          if(meterTypes.indexOf('day') !== -1 && meterTypes.indexOf('injectionDay') === -1 )
            meterTypes.push('injectionDay', 'injectionNight')
        }
      } else {
        this.removeMeterTypes(["injectionBase", "injectionDay", "injectionNight"])
      }

      this.$store.commit("comparison/reset");
      this.$store.commit("consumption/setValid", false);
      this.$store.commit("consumption/setMeterType", meterTypes);
    },
    computeScenario() {
      this.scenario = "PURE_OFFTAKE"; //!this.hasSolarPanels || !this.hasElec
      if (this.hasElec && this.hasSolarPanels) {
        if (this.isBxl
        || (this.isFlanders && this.hasSmartMeter)
        || (this.isWallonia && !this.solarPanelsCertification)) {
          this.$store.commit("consumption/setKnowNetConsumption", true);
          this.scenario = "COMM_OFFTAKE_AND_INJECTION";
          if(this.hasMeterType('mono')){
            if(this.hasMeterType('exclusiveNight'))
              this.setMeterType(['mono', 'injectionBase', 'exclusiveNight'])
            else
              this.setMeterType(['mono', 'injectionBase'])
          }else{
            if(this.hasMeterType('exclusiveNight'))
              this.setMeterType(['day', 'night', 'injectionDay', 'injectionNight', 'exclusiveNight'])
            else
              this.setMeterType(['day', 'night', 'injectionDay', 'injectionNight'])
          }
        } else { // this.isWallonia || this.isFlanders
          this.scenario = "COMPENSATED_OFFTAKE";
        }
      }
      if(this.scenario !== "COMM_OFFTAKE_AND_INJECTION")
        this.removeMeterTypes(["injectionBase", "injectionDay", "injectionNight"])
      this.$store.commit("consumption/setSolarPanelsScenario", this.scenario);
    }
  },
  created() {
    if (!this.fromFlanders && this.hasSmartMeter) {
      this.$store.commit("consumption/setHasSmartMeter", false);
      this.$store.commit("consumption/setHasQuarterRegime", false);
      this.$store.commit("consumption/setKnowPeakLevel", false);
      this.$store.commit("consumption/setPeakLevel", null);
    }

    this.computeScenario();
  },
  components: {
    EnergyButton,
    CheckButton,
    InputField,
    SwitchField
  }
};
</script>

<style lang="scss" scoped>
.input-error {
  max-width: 250px;
}
</style>



// WEBPACK FOOTER //
// src/views/consumption/MeterType.vue